import { useEffect, useMemo, useRef, useState } from "react";
import Topbar from "../components/Topbar";
import { useAuth } from "../context/AuthContext";
import { listHotels } from "../api/hotels";
import type { HotelOut } from "../types/hotels";
import { getHotelEmbed } from "../api/metabase";
import DataUnavailableCard, { TECHNICAL_ERROR_MESSAGE } from "./analyticsDashboard/components/DataUnavailableCard";
import SelectedHotelMinStayCard from "./analyticsDashboard/components/SelectedHotelMinStayCard";
import { LAST_PARSING_DATE_LABEL, useLastParsingDate } from "./analyticsDashboard/hooks/useLastParsingDate";
import { getSubscriptionState } from "../utils/subscription";

const EMBED_TYPE: "categories" = "categories";

export default function AnalyticsCategoriesPage() {
  const { user, company_id: companyId } = useAuth();
  const subscriptionState = getSubscriptionState(user?.subscription_expires_on);
  const isSubscriptionExpired = subscriptionState.isExpired;

  const [hotels, setHotels] = useState<HotelOut[]>([]);
  const [hotelsLoading, setHotelsLoading] = useState(false);
  const [hotelsError, setHotelsError] = useState<string | null>(null);
  const [selectedHotelId, setSelectedHotelId] = useState<number | null>(null);

  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshIndex, setRefreshIndex] = useState(0);
  const frameWrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!companyId) return;
    let isMounted = true;
    setHotelsLoading(true);
    setHotelsError(null);

    listHotels(companyId, { page: 1, per_page: 200 })
      .then(data => {
        if (!isMounted) return;
        setHotels(data.items);
        setSelectedHotelId(prev => {
          if (data.items.length === 1) return data.items[0].id;
          if (!prev) return null;
          return data.items.some(hotel => hotel.id === prev) ? prev : null;
        });
      })
      .catch((err: any) => {
        if (!isMounted) return;
        setHotelsError(err?.response?.data?.detail || "Не удалось загрузить список объектов");
      })
      .finally(() => {
        if (isMounted) setHotelsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [companyId]);

  useEffect(() => {
    if (!selectedHotelId || isSubscriptionExpired) {
      setIframeUrl(null);
      setError(null);
      setLoading(false);
      return;
    }

    let isMounted = true;
    setLoading(true);
    setError(null);

    getHotelEmbed(selectedHotelId, EMBED_TYPE)
      .then(payload => {
        if (!isMounted) return;
        setIframeUrl(payload.url);
      })
      .catch((err: any) => {
        if (!isMounted) return;
        setError(err?.response?.data?.detail || "Не удалось загрузить дашборд");
        setIframeUrl(null);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [isSubscriptionExpired, selectedHotelId, refreshIndex]);

  const handleRefresh = () => {
    if (!selectedHotelId || loading || isSubscriptionExpired) return;
    setRefreshIndex(prev => prev + 1);
  };

  const selectedHotel = useMemo(() => hotels.find(h => h.id === selectedHotelId) || null, [hotels, selectedHotelId]);
  const lastParsingDate = useLastParsingDate(selectedHotel?.group_id ?? null, 1, !isSubscriptionExpired);
  const canShowEmbed = Boolean(iframeUrl && selectedHotelId);
  const placeholder = "Выберите объект размещения, чтобы увидеть разрез по категориям.";

  const selectorSkeleton = (
    <div className="space-y-3">
      <div className="h-4 w-40 rounded-full bg-slate-200/70 animate-pulse" />
      <div className="h-4 w-56 rounded-full bg-slate-200/60 animate-pulse" />
      <div className="h-14 rounded-2xl bg-slate-100/80 animate-pulse" />
      <div className="h-4 w-28 rounded-full bg-slate-200/70 animate-pulse" />
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 text-slate-900">
      <Topbar />

      <main className="px-6 py-10">
        <div className="mx-auto max-w-[1200px] space-y-8">
          <section className="rounded-3xl border border-white/70 bg-white/90 p-8 shadow-[0_18px_40px_rgba(15,23,42,0.08)] backdrop-blur">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h1 className="mt-2 text-3xl font-bold text-slate-900">По категориям</h1>
                <p className="mt-2 text-sm text-slate-500">
                  Сравнивайте показатели по категориям и типам размещения выбранного объекта.
                </p>
              </div>
              <div className="rounded-2xl border border-indigo-100 bg-indigo-50/70 px-6 py-4 text-sm text-indigo-700">
                {lastParsingDate ? `${LAST_PARSING_DATE_LABEL}: ${lastParsingDate}` : selectedHotel
                  ? `Выбран объект: ${selectedHotel.name} · ${selectedHotel.city}`
                  : "Выберите объект, чтобы увидеть данные"}
              </div>
            </div>
          </section>

          <section className="rounded-3xl border border-white/70 bg-white/90 p-6 shadow-[0_14px_38px_rgba(15,23,42,0.07)] backdrop-blur space-y-4">
            {hotelsLoading && selectorSkeleton}
            {!companyId && <div className="text-slate-500">Для вашей учетной записи не найдено компании.</div>}

            {!hotelsLoading && !hotelsError && companyId && hotels.length > 1 && (
              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-slate-700" htmlFor="categories-hotel-select">
                  Объект размещения
                </label>
                <select
                  id="categories-hotel-select"
                  className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-lg text-slate-800 shadow-inner focus:border-indigo-300 focus:outline-none focus:ring-4 focus:ring-indigo-200/60"
                  value={selectedHotelId ?? ""}
                  onChange={e => setSelectedHotelId(e.target.value ? Number(e.target.value) : null)}
                >
                  <option value="">— Выберите объект —</option>
                  {hotels.map(hotel => (
                    <option key={hotel.id} value={hotel.id}>
                      {hotel.name} · {hotel.city}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {!hotelsLoading && !hotelsError && companyId && hotels.length === 0 && (
              <div className="text-slate-500">Нет объектов, доступных для просмотра аналитики.</div>
            )}
          </section>

          {(hotelsError || error) && <DataUnavailableCard message={TECHNICAL_ERROR_MESSAGE} variant="error" />}

          <SelectedHotelMinStayCard minStay={selectedHotel?.min_stay} />

          {!isSubscriptionExpired && loading && (
            <div className="space-y-4 rounded-3xl border border-slate-200 bg-white/90 px-6 py-6">
              <div className="h-4 w-40 rounded-full bg-slate-200/80 animate-pulse" />
              <div className="h-[70vh] min-h-[480px] rounded-2xl bg-slate-100/80 animate-pulse" />
            </div>
          )}

          {!isSubscriptionExpired && !loading && !error && !selectedHotelId && hotels.length > 0 && (
            <div className="rounded-3xl border border-dashed border-slate-300 bg-white/90 px-6 py-10 text-center text-slate-500">
              {placeholder}
            </div>
          )}

          {!isSubscriptionExpired && !loading && canShowEmbed && (
            <div className="space-y-4 rounded-3xl border border-white/70 bg-white/95 p-4 shadow-[0_18px_45px_rgba(15,23,42,0.12)] backdrop-blur">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-sm text-slate-500">Отчет для:</p>
                  <p className="text-lg font-semibold text-slate-900">{selectedHotel?.name}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-1.5 text-sm text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                    onClick={handleRefresh}
                    disabled={!selectedHotelId || loading}
                  >
                    Обновить дашборд
                  </button>
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-1.5 text-sm text-slate-600 transition hover:bg-slate-50"
                    onClick={() => frameWrapperRef.current?.requestFullscreen?.()}
                  >
                    Полный экран
                  </button>
                </div>
              </div>

              <div ref={frameWrapperRef} className="overflow-hidden rounded-2xl border border-slate-100">
                <iframe
                  title="Categories Metabase report"
                  src={iframeUrl ?? ""}
                  className="h-[90vh] min-h-[640px] w-full"
                  key={refreshIndex}
                  loading="lazy"
                  allowFullScreen
                />
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
